/**
 * Admin console › Permit equipment › Datasheet library (docs/features/permit-plan-sets.md §12a): every manifest
 * entry (app/design/permits/data/datasheets.yaml) and entry staff added, with its status (cached, changed, broken,
 * blocked, flagged, link only, not fetched yet), revision history, the last check and "Refresh now" (at most hourly;
 * workers check monthly). Staff add an entry by URL. Viewing a stored revision opens a link signed per click
 * (15 minutes): the files are never public.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Eye, History, Plus, RefreshCw } from "lucide-react";
import { useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { api, errorMessage, unwrap, type S } from "../../api/client";
import { Alert, Badge, Button, Card, Field, Modal, Select, Spinner, TextInput, useToast } from "../../components/ui";
import { useT, type Translate } from "../../i18n/I18nProvider";
import type { MessageKey } from "../../i18n/keys";
import { DirIcon } from "../../i18n/mirror";
import { useFmt, type Fmt } from "./format";

type Entry = S["DatasheetEntry"];
type Status = S["DatasheetStatus"];
type Kind = S["DatasheetKind"];

const STATUS: Record<Status, { label: MessageKey; tone?: "ok" | "warn" | "danger" | "info" }> = {
  cached: { label: "admin.datasheetLibrary.statusCached", tone: "ok" },
  changed: { label: "admin.datasheetLibrary.statusChanged", tone: "warn" },
  broken: { label: "admin.datasheetLibrary.statusBroken", tone: "danger" },
  blocked: { label: "admin.datasheetLibrary.statusBlocked", tone: undefined },
  flagged: { label: "admin.datasheetLibrary.statusFlagged", tone: "warn" },
  link_only: { label: "admin.datasheetLibrary.statusLinkOnly", tone: undefined },
  pending: { label: "admin.datasheetLibrary.statusPending", tone: "info" },
};
const KINDS: Kind[] = ["module", "micro", "inverter", "optimizer", "battery", "rsd", "rail", "attachment", "install_manual", "span_table", "cert_letter"];
const KIND_LABEL: Record<Kind, MessageKey> = {
  module: "admin.datasheetLibrary.kindModule",
  micro: "admin.datasheetLibrary.kindMicro",
  inverter: "admin.datasheetLibrary.kindInverter",
  optimizer: "admin.datasheetLibrary.kindOptimizer",
  battery: "admin.datasheetLibrary.kindBattery",
  rsd: "admin.datasheetLibrary.kindRsd",
  rail: "admin.datasheetLibrary.kindRail",
  attachment: "admin.datasheetLibrary.kindAttachment",
  install_manual: "admin.datasheetLibrary.kindInstallManual",
  span_table: "admin.datasheetLibrary.kindSpanTable",
  cert_letter: "admin.datasheetLibrary.kindCertLetter",
};
const RUN_TONES = { succeeded: "ok", partial: "warn", failed: "danger", queued: "info", running: "info" } as const;

/** A file size: megabytes from 1 MB, else kilobytes (at least 1). */
function fileSize(bytes: number | null | undefined, t: Translate, fmt: Fmt): string {
  if (bytes == null) return "—";
  return bytes >= 1024 * 1024
    ? t("admin.datasheetLibrary.sizeMb", { size: fmt.num(bytes / 1024 / 1024, 1) })
    : t("admin.datasheetLibrary.sizeKb", { size: fmt.num(Math.max(1, Math.round(bytes / 1024))) });
}

function useViewRevision() {
  const toast = useToast();
  return useMutation({
    mutationFn: (revisionId: string) => unwrap(api.GET("/v1/admin/datasheet-library/revisions/{revision_id}/link", { params: { path: { revision_id: revisionId } } })),
    onSuccess: (link) => window.open(link.url, "_blank", "noopener"),
    onError: (e) => toast(errorMessage(e), true),
  });
}

export function DatasheetLibrary() {
  const qc = useQueryClient();
  const toast = useToast();
  const t = useT();
  const fmt = useFmt();
  const { date, num } = fmt;
  const [status, setStatus] = useState<Status | "all">("all");
  const [kind, setKind] = useState<Kind | "all">("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const library = useQuery({
    queryKey: ["datasheet-library"],
    queryFn: () => unwrap(api.GET("/v1/admin/datasheet-library")),
    refetchInterval: (query) => (["queued", "running"].includes(query.state.data?.latest?.status ?? "") ? 5000 : false),
  });
  const refresh = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/admin/datasheet-library/refresh")),
    onSuccess: (data) => {
      qc.setQueryData(["datasheet-library"], data);
      toast(t("admin.datasheetLibrary.refreshQueued"));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const view = useViewRevision();
  const data = library.data;
  const entries = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data?.entries ?? []).filter(
      (e) =>
        (status === "all" || e.status === status) &&
        (kind === "all" || e.kind === kind) &&
        (!needle || `${e.manufacturer} ${e.series} ${e.cec_models.join(" ")} ${e.covers ?? ""}`.toLowerCase().includes(needle)),
    );
  }, [data, status, kind, q]);
  const run = data?.latest;
  return (
    <div style={{ marginTop: 16 }} data-testid="datasheet-library">
      <Card
        title={t("admin.datasheetLibrary.title")}
        description={t("admin.datasheetLibrary.description", { monthly: data?.monthly === false ? "off" : "on" })}
        actions={
          <div className="row" style={{ gap: 8 }}>
            <Button icon={<Plus />} onClick={() => setAdding(true)}>
              {t("admin.datasheetLibrary.add")}
            </Button>
            <Button icon={<RefreshCw />} loading={refresh.isPending} disabled={!data || !!data.next_allowed_at} onClick={() => refresh.mutate()}>
              {t("admin.datasheetLibrary.refresh")}
            </Button>
          </div>
        }
        pad={false}
      >
        {library.isLoading ? (
          <Spinner />
        ) : library.error ? (
          <Alert tone="danger">{errorMessage(library.error)}</Alert>
        ) : data ? (
          <>
            <div className="ds-summary">
              <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
                {(Object.keys(STATUS) as Status[])
                  .filter((s) => data.by_status[s])
                  .map((s) => (
                    <button key={s} type="button" className={`ds-chip${status === s ? " on" : ""}`} onClick={() => setStatus(status === s ? "all" : s)}>
                      <Badge tone={STATUS[s].tone}>{t(STATUS[s].label)}</Badge> {num(data.by_status[s])}
                    </button>
                  ))}
              </div>
              <div className="small muted">
                {run ? (
                  <>
                    <Badge tone={RUN_TONES[run.status]}>{t("admin.datasheetLibrary.runStatus", { status: run.status })}</Badge>{" "}
                    {t(run.finished_at ? "admin.datasheetLibrary.runFinished" : "admin.datasheetLibrary.runStarted", {
                      trigger: run.trigger === "monthly" || run.trigger === "entry" ? run.trigger : run.requested_by ? "person" : "staff",
                      who: run.requested_by ?? "",
                      created: date(run.created_at, true),
                      finished: date(run.finished_at, true),
                      outcomes:
                        Object.entries(run.counts)
                          .map(([k, n]) => t("admin.datasheetLibrary.outcomeCount", { outcome: k, count: n }))
                          .join(", ") || t("admin.datasheetLibrary.nothingChecked"),
                    })}
                  </>
                ) : (
                  t("admin.datasheetLibrary.noRun")
                )}
                {data.next_allowed_at && <> · {t("admin.datasheetLibrary.nextAllowed", { at: date(data.next_allowed_at, true) })}</>}
              </div>
            </div>
            <div className="ds-filters">
              <TextInput value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("admin.datasheetLibrary.search")} aria-label={t("admin.datasheetLibrary.search")} />
              <Select<Kind | "all">
                value={kind}
                onChange={setKind}
                options={[{ value: "all", label: t("admin.datasheetLibrary.allKinds") }, ...KINDS.map((k) => ({ value: k, label: t(KIND_LABEL[k]) }))]}
                ariaLabel={t("admin.datasheetLibrary.kind")}
              />
              <Select<Status | "all">
                value={status}
                onChange={setStatus}
                options={[{ value: "all", label: t("admin.datasheetLibrary.allStatuses") }, ...(Object.keys(STATUS) as Status[]).map((s) => ({ value: s, label: t(STATUS[s].label) }))]}
                ariaLabel={t("admin.datasheetLibrary.status")}
              />
            </div>
            <div className="table-wrap">
              <table className="table ds-table">
                <thead>
                  <tr>
                    <th>{t("admin.datasheetLibrary.colDocument")}</th>
                    <th>{t("admin.datasheetLibrary.kind")}</th>
                    <th>{t("admin.datasheetLibrary.status")}</th>
                    <th>{t("admin.datasheetLibrary.colStoredCopy")}</th>
                    <th>{t("admin.datasheetLibrary.colLastCheck")}</th>
                    <th aria-label={t("admin.datasheetLibrary.colActions")} />
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e) => (
                    <tr key={e.key} className={e.retired ? "faint" : undefined}>
                      <td dir="auto">
                        <b>{e.manufacturer}</b> {e.series}
                        <div className="small muted">
                          {[e.doc, e.regions.join("/"), e.state, e.asce && t("admin.datasheetLibrary.asceEdition", { edition: e.asce }), e.version].filter(Boolean).join(" · ")}
                        </div>
                        {(e.status === "broken" || e.status === "blocked" || e.status === "flagged") && (e.last_error || e.note) && <div className="small ds-why">{e.last_error ?? e.note}</div>}
                      </td>
                      <td>{t(KIND_LABEL[e.kind])}</td>
                      <td>
                        <Badge tone={STATUS[e.status].tone}>{t(STATUS[e.status].label)}</Badge>
                        {e.moved_to && <div className="small muted">{t("admin.datasheetLibrary.moved")}</div>}
                      </td>
                      <td className="small">
                        {e.current_sha256 ? (
                          <>
                            <code dir="ltr">{e.current_sha256.slice(0, 10)}</code>
                            <div>
                              {e.pages == null
                                ? t("admin.datasheetLibrary.sizeOnly", { size: fileSize(e.size_bytes, t, fmt) })
                                : t("admin.datasheetLibrary.pagesAndSize", { pages: e.pages, size: fileSize(e.size_bytes, t, fmt) })}
                            </div>
                            <div className="muted">{t("admin.datasheetLibrary.revisionCount", { count: e.revision_count })}</div>
                          </>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td className="small">{date(e.last_checked_at, true)}</td>
                      <td>
                        <div className="ds-actions">
                          {e.current_revision_id && (
                            <Button size="sm" variant="ghost" icon={<Eye size={14} />} title={t("admin.datasheetLibrary.view")} aria-label={t("admin.datasheetLibrary.viewDocument", { name: `${e.manufacturer} ${e.series}` })} loading={view.isPending && view.variables === e.current_revision_id} onClick={() => view.mutate(e.current_revision_id!)} />
                          )}
                          {e.product_url && (
                            <a className="btn btn-sm btn-ghost" href={e.product_url} target="_blank" rel="noopener noreferrer" title={t("admin.datasheetLibrary.official")} aria-label={t("admin.datasheetLibrary.officialFor", { name: `${e.manufacturer} ${e.series}` })}>
                              <DirIcon><ExternalLink size={14} /></DirIcon>
                            </a>
                          )}
                          <Button size="sm" variant="ghost" icon={<History size={14} />} onClick={() => setOpen(e.key)}>
                            {t("admin.datasheetLibrary.history")}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="small muted ds-foot">
              {t("admin.datasheetLibrary.shown", {
                shown: entries.length,
                total: data.entries.length,
                kinds: Object.entries(data.by_kind)
                  .map(([k, n]) => t("admin.datasheetLibrary.kindCount", { kind: KIND_LABEL[k as Kind] ? t(KIND_LABEL[k as Kind]) : k, count: n }))
                  .join(" · "),
              })}
            </div>
          </>
        ) : null}
      </Card>
      {open && <EntryModal entryKey={open} onClose={() => setOpen(null)} />}
      {adding && <AddEntry onClose={() => setAdding(false)} />}
    </div>
  );
}

function EntryModal({ entryKey, onClose }: { entryKey: string; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const t = useT();
  const fmt = useFmt();
  const { date } = fmt;
  const entry = useQuery({
    queryKey: ["datasheet-entry", entryKey],
    queryFn: () => unwrap(api.GET("/v1/admin/datasheet-library/entries/{entry_key}", { params: { path: { entry_key: entryKey } } })),
  });
  const view = useViewRevision();
  const save = useMutation({
    mutationFn: (body: S["DatasheetEntryUpdate"]) => unwrap(api.PATCH("/v1/admin/datasheet-library/entries/{entry_key}", { params: { path: { entry_key: entryKey } }, body })),
    onSuccess: (data) => {
      qc.setQueryData(["datasheet-entry", entryKey], data);
      qc.invalidateQueries({ queryKey: ["datasheet-library"] });
      toast(t("admin.common.saved"));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const e: Entry | undefined = entry.data;
  return (
    <Modal title={e ? <span dir="auto">{`${e.manufacturer} ${e.series}`}</span> : t("admin.datasheetLibrary.entryTitle")} onClose={onClose} width={760}>
      {entry.isLoading || !e ? (
        <Spinner />
      ) : (
        <div className="stack">
          <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
            <Badge tone={STATUS[e.status].tone}>{t(STATUS[e.status].label)}</Badge>
            <Badge>{t(KIND_LABEL[e.kind])}</Badge>
            <Badge>{e.source === "manifest" ? t("admin.datasheetLibrary.sourceManifest") : t("admin.datasheetLibrary.sourceStaff")}</Badge>
            {e.host && (
              <Badge>
                {e.host === "own" ? t("admin.datasheetLibrary.hostOwn") : e.host === "cdn" ? t("admin.datasheetLibrary.hostCdn") : <span dir="auto">{e.host}</span>}
              </Badge>
            )}
          </div>
          <div className="small" dir="auto">
            {e.covers && <div>{e.covers}</div>}
            {e.note && <div className="muted">{e.note}</div>}
            {e.last_error && (
              <div className="ds-why">
                {e.last_http_status
                  ? t("admin.datasheetLibrary.lastErrorHttp", { error: e.last_error, status: e.last_http_status })
                  : t("admin.datasheetLibrary.lastError", { error: e.last_error })}
              </div>
            )}
            {e.moved_to && <div className="muted">{t("admin.datasheetLibrary.movedTo", { url: e.moved_to })}</div>}
          </div>
          <div className="small">
            {e.url && (
              <div>
                <FormattedMessage id="admin.datasheetLibrary.pdfLink" values={{ link: <a href={e.url} target="_blank" rel="noopener noreferrer" dir="ltr">{e.url}</a> }} />
              </div>
            )}
            {e.product_url && (
              <div>
                <FormattedMessage id="admin.datasheetLibrary.officialLink" values={{ link: <a href={e.product_url} target="_blank" rel="noopener noreferrer" dir="ltr">{e.product_url}</a> }} />
              </div>
            )}
            {e.manifest_sha256 && (
              <div className="muted">
                <FormattedMessage
                  id="admin.datasheetLibrary.manifestChecked"
                  values={{ date: date(e.checked_on), hash: <code dir="ltr">{e.manifest_sha256.slice(0, 16)}</code>, version: e.version ?? "", hasVersion: e.version ? "yes" : "no" }}
                />
              </div>
            )}
            {!!e.cec_models.length && <div className="muted" dir="auto">{t("admin.datasheetLibrary.cecModels", { models: e.cec_models.join(", ") })}</div>}
            {!!e.equipment_keys.length && <div className="muted" dir="auto">{t("admin.datasheetLibrary.catalogue", { keys: e.equipment_keys.join(", ") })}</div>}
            {!!e.racking.length && <div className="muted" dir="auto">{t("admin.datasheetLibrary.racking", { items: e.racking.map((r) => `${r.kind} ${r.key}`).join(", ") })}</div>}
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t("admin.datasheetLibrary.colRevision")}</th>
                  <th>{t("admin.datasheetLibrary.colPages")}</th>
                  <th>{t("admin.datasheetLibrary.colFirstSeen")}</th>
                  <th>{t("admin.datasheetLibrary.colLastSeen")}</th>
                  <th aria-label={t("admin.datasheetLibrary.colActions")} />
                </tr>
              </thead>
              <tbody>
                {(e.revisions ?? []).map((r) => (
                  <tr key={r.id}>
                    <td className="small">
                      <code dir="ltr">{r.sha256.slice(0, 16)}</code> {r.current && <Badge tone="ok">{t("admin.datasheetLibrary.current")}</Badge>}
                      <div className="muted">
                        {r.outline_items
                          ? t("admin.datasheetLibrary.revisionMetaBookmarks", { size: fileSize(r.size_bytes, t, fmt), trigger: r.trigger, count: r.outline_items })
                          : t("admin.datasheetLibrary.revisionMeta", { size: fileSize(r.size_bytes, t, fmt), trigger: r.trigger })}
                      </div>
                    </td>
                    <td>{fmt.num(r.pages)}</td>
                    <td className="small">{date(r.first_seen_at, true)}</td>
                    <td className="small">{date(r.last_seen_at, true)}</td>
                    <td>
                      <Button size="sm" variant="ghost" icon={<Eye size={14} />} onClick={() => view.mutate(r.id)}>
                        {t("admin.datasheetLibrary.view")}
                      </Button>
                    </td>
                  </tr>
                ))}
                {!e.revisions?.length && (
                  <tr>
                    <td colSpan={5} className="small muted">
                      {t("admin.datasheetLibrary.noStoredCopy")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="row" style={{ gap: 8 }}>
            {e.url && (e.fetch === "flagged" || e.fetch === "blocked") && (
              <Button loading={save.isPending} onClick={() => save.mutate({ fetch: "auto" })}>
                {t("admin.datasheetLibrary.allow")}
              </Button>
            )}
            {e.fetch === "auto" && (
              <Button variant="ghost" loading={save.isPending} onClick={() => save.mutate({ fetch: "flagged" })}>
                {t("admin.datasheetLibrary.stop")}
              </Button>
            )}
            {e.source === "admin" && (
              <Button variant="ghost" loading={save.isPending} onClick={() => save.mutate({ retired: !e.retired })}>
                {e.retired ? t("admin.datasheetLibrary.restore") : t("admin.datasheetLibrary.retire")}
              </Button>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

function AddEntry({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const t = useT();
  const [form, setForm] = useState({ url: "", product_url: "", manufacturer: "", series: "", kind: "module" as Kind, regions: "US", covers: "", cec_models: "" });
  const add = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/v1/admin/datasheet-library/entries", {
          body: {
            url: form.url.trim(),
            product_url: form.product_url.trim(),
            manufacturer: form.manufacturer.trim(),
            series: form.series.trim(),
            kind: form.kind,
            regions: form.regions.split(/[\s,]+/).filter(Boolean).map((r) => r.toUpperCase()),
            covers: form.covers.trim() || null,
            cec_models: form.cec_models.split(",").map((m) => m.trim()).filter(Boolean),
          },
        }),
      ),
    onSuccess: (e) => {
      qc.invalidateQueries({ queryKey: ["datasheet-library"] });
      toast(e.status === "flagged" ? t("admin.datasheetLibrary.addedFlagged") : t("admin.datasheetLibrary.addedQueued"));
      onClose();
    },
  });
  const set = (k: keyof typeof form) => (ev: { target: { value: string } }) => setForm({ ...form, [k]: ev.target.value });
  return (
    <Modal
      title={t("admin.datasheetLibrary.add")}
      onClose={onClose}
      width={640}
      footer={
        <>
          <Button onClick={onClose}>{t("admin.common.cancel")}</Button>
          <Button variant="primary" loading={add.isPending} disabled={!form.url.trim() || !form.product_url.trim() || !form.manufacturer.trim() || !form.series.trim()} onClick={() => add.mutate()}>
            {t("admin.common.add")}
          </Button>
        </>
      }
    >
      <div className="stack">
        {add.error && <Alert tone="danger">{errorMessage(add.error)}</Alert>}
        <Field label={t("admin.datasheetLibrary.fieldPdf")} hint={t("admin.datasheetLibrary.fieldPdfHint")}>
          <TextInput value={form.url} onChange={set("url")} placeholder={t("admin.datasheetLibrary.pdfPlaceholder")} />
        </Field>
        <Field label={t("admin.datasheetLibrary.fieldProductPage")} hint={t("admin.datasheetLibrary.fieldProductPageHint")}>
          <TextInput value={form.product_url} onChange={set("product_url")} placeholder={t("admin.datasheetLibrary.urlPlaceholder")} />
        </Field>
        <div className="form-grid">
          <Field label={t("admin.datasheetLibrary.fieldManufacturer")}>
            <TextInput value={form.manufacturer} onChange={set("manufacturer")} />
          </Field>
          <Field label={t("admin.datasheetLibrary.fieldSeries")}>
            <TextInput value={form.series} onChange={set("series")} />
          </Field>
          <Field label={t("admin.datasheetLibrary.kind")}>
            <Select<Kind> value={form.kind} onChange={(k) => setForm({ ...form, kind: k })} options={KINDS.map((k) => ({ value: k, label: t(KIND_LABEL[k]) }))} />
          </Field>
          <Field label={t("admin.datasheetLibrary.fieldRegions")} hint={t("admin.datasheetLibrary.fieldRegionsHint")}>
            <TextInput value={form.regions} onChange={set("regions")} />
          </Field>
        </div>
        <Field label={t("admin.datasheetLibrary.fieldCecModels")} hint={t("admin.datasheetLibrary.fieldCecModelsHint")}>
          <TextInput value={form.cec_models} onChange={set("cec_models")} />
        </Field>
        <Field label={t("admin.datasheetLibrary.fieldCovers")}>
          <TextInput value={form.covers} onChange={set("covers")} maxLength={300} />
        </Field>
      </div>
    </Modal>
  );
}
